import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Upload, FileSpreadsheet, History, Plus, Download, RefreshCw, 
  AlertCircle, CheckCircle, XCircle, Clock, ChevronLeft, Trash2
} from 'lucide-react';

// File Upload Component
function FileUpload({ onUploadSuccess }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };
  
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };
  
  const uploadFile = async (file) => {
    // Validate file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExt)) {
      setError(`Invalid file type. Supported formats: ${validExtensions.join(', ')}`);
      return;
    }
    
    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post('/imports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(progress);
        }
      });
      
      onUploadSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
          dragging 
            ? 'border-purple-500 bg-purple-50' 
            : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="file-drop-zone"
      >
        <Upload className={`w-16 h-16 mx-auto mb-4 ${dragging ? 'text-purple-500' : 'text-gray-400'}`} />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {uploading ? 'Uploading...' : 'Drop your file here'}
        </h3>
        <p className="text-gray-600 mb-4">or click to browse</p>
        
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          className="hidden"
          id="file-input"
          disabled={uploading}
        />
        <label
          htmlFor="file-input"
          className={`inline-block px-6 py-3 rounded-lg font-medium cursor-pointer transition ${
            uploading 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-purple-600 text-white hover:bg-purple-700'
          }`}
        >
          {uploading ? `Uploading ${uploadProgress}%` : 'Select File'}
        </label>
        
        <p className="text-sm text-gray-500 mt-4">
          Supported formats: Excel (.xlsx, .xls), CSV (.csv) • Max 50MB
        </p>
      </div>
      
      {/* Upload Progress */}
      {uploading && (
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
            <span className="text-purple-700 font-medium">Uploading file...</span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Upload Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Import Preview & Column Mapping Component
function ImportPreview({ importData, onBack, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadPreview();
  }, [importData.id]);
  
  const loadPreview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/imports/${importData.id}/preview`);
      setPreview(response.data);
      setColumnMapping(response.data.suggested_mapping || {});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMappingChange = (column, metricName) => {
    setColumnMapping(prev => ({
      ...prev,
      [column]: metricName
    }));
    setValidationResult(null);
  };
  
  const validateMapping = async () => {
    try {
      setValidating(true);
      setError('');
      
      const response = await axios.post(`/imports/${importData.id}/map-columns`, {
        column_mapping: columnMapping
      });
      
      setValidationResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };
  
  const confirmImport = async (skipErrors = false) => {
    try {
      setImporting(true);
      setError('');
      
      await axios.post(`/imports/${importData.id}/confirm`, {
        skip_errors: skipErrors
      });
      
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Map Columns</h3>
          <p className="text-sm text-gray-600">
            {preview?.total_rows || 0} rows found • Map your file columns to metrics
          </p>
        </div>
      </div>
      
      {/* Column Mapping */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h4 className="font-medium text-gray-900 mb-4">Column Mapping</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {preview?.columns?.map((column) => (
            <div key={column} className="flex items-center gap-3">
              <div className="flex-1 bg-white px-4 py-2 rounded-lg border border-gray-200">
                <span className="text-sm text-gray-500">File Column:</span>
                <p className="font-medium text-gray-900">{column}</p>
              </div>
              <span className="text-gray-400">→</span>
              <select
                value={columnMapping[column] || ''}
                onChange={(e) => handleMappingChange(column, e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Skip Column --</option>
                {preview?.suggested_mapping && Object.values(preview.suggested_mapping).map(metric => (
                  <option key={metric} value={metric}>{metric}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        
        <button
          onClick={validateMapping}
          disabled={validating || Object.values(columnMapping).filter(Boolean).length === 0}
          className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {validating ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Validating...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Validate Mapping
            </>
          )}
        </button>
      </div>
      
      {/* Data Preview */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Data Preview (first 10 rows)</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {preview?.columns?.map((column) => (
                  <th key={column} className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {preview?.preview_rows?.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  {preview.columns.map((column) => (
                    <td key={column} className="px-4 py-3 text-sm text-gray-900">
                      {row[column]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Validation Result */}
      {validationResult && (
        <div className={`rounded-xl p-6 ${validationResult.valid ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="flex items-start gap-3">
            {validationResult.valid ? (
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h4 className={`font-semibold ${validationResult.valid ? 'text-green-700' : 'text-yellow-700'}`}>
                {validationResult.valid ? 'Validation Passed' : 'Validation Warnings'}
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                {validationResult.valid_rows || 0} valid rows • {validationResult.error_rows || 0} rows with errors
              </p>
              
              {validationResult.errors?.length > 0 && (
                <div className="mt-4 max-h-48 overflow-y-auto">
                  {validationResult.errors.slice(0, 10).map((err, idx) => (
                    <div key={idx} className="text-sm text-red-600 mb-1">
                      Row {err.row}: {err.message}
                    </div>
                  ))}
                  {validationResult.errors.length > 10 && (
                    <p className="text-sm text-gray-500">
                      ...and {validationResult.errors.length - 10} more errors
                    </p>
                  )}
                </div>
              )}
              
              <div className="mt-4 flex gap-3">
                {validationResult.valid ? (
                  <button
                    onClick={() => confirmImport(false)}
                    disabled={importing}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Import All Data'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => confirmImport(true)}
                      disabled={importing}
                      className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50"
                    >
                      {importing ? 'Importing...' : 'Import Valid Rows Only'}
                    </button>
                    <button
                      onClick={onBack}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}

// Manual Entry Component
function ManualEntry() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [value, setValue] = useState('');
  const [periodStart, setPeriodStart] = useState(new Date().toISOString().split('T')[0]);
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadMetrics();
  }, []);
  
  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/metrics');
      setMetrics(response.data.filter(m => m.is_active));
    } catch (err) {
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedMetric || !value) {
      setError('Please select a metric and enter a value');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      await axios.post(`/manual-entry/metrics/${selectedMetric}/values`, {
        value: parseFloat(value),
        period_start: new Date(periodStart).toISOString(),
        period_end: new Date(periodEnd).toISOString(),
        notes: notes || null,
        is_draft: false
      });
      
      setSuccess('Metric value saved successfully!');
      setValue('');
      setNotes('');
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to save metric value');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Metric Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Metric</label>
          <select
            value={selectedMetric || ''}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            data-testid="metric-select"
          >
            <option value="">-- Select a metric --</option>
            {metrics.map(metric => (
              <option key={metric.id} value={metric.id}>
                {metric.name} ({metric.data_type})
              </option>
            ))}
          </select>
        </div>
        
        {/* Value */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
          <input
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter value"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            data-testid="value-input"
          />
        </div>
        
        {/* Period */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Period Start</label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Period End</label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        
        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this value..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
        </div>
        
        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-700">{success}</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}
        
        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 px-6 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="submit-manual-entry"
        >
          {submitting ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Save Metric Value
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// Import History Component
function ImportHistory() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadHistory();
  }, []);
  
  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/imports/history?limit=50');
      setImports(response.data);
    } catch (err) {
      setError('Failed to load import history');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRollback = async (importId) => {
    if (!window.confirm('Are you sure you want to rollback this import? This will delete all imported values.')) {
      return;
    }
    
    try {
      await axios.post(`/imports/${importId}/rollback`);
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Rollback failed');
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
      case 'validating':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'rolled_back':
        return <Trash2 className="w-5 h-5 text-gray-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
      case 'validating':
        return 'bg-yellow-100 text-yellow-800';
      case 'rolled_back':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }
  
  if (imports.length === 0) {
    return (
      <div className="text-center py-12">
        <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Import History</h3>
        <p className="text-gray-600">Your import history will appear here once you upload files.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">File</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Type</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Records</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Date</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {imports.map((imp) => (
              <tr key={imp.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-900 font-medium">{imp.source || 'Unknown'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600 capitalize">{imp.import_type}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(imp.status)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(imp.status)}`}>
                      {imp.status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {imp.records_imported || 0} / {imp.records_processed || 0}
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {new Date(imp.started_at).toLocaleString()}
                </td>
                <td className="px-6 py-4">
                  {imp.status === 'completed' && (
                    <button
                      onClick={() => handleRollback(imp.id)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                      data-testid={`rollback-${imp.id}`}
                    >
                      Rollback
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Templates Component
function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadTemplates();
  }, []);
  
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/imports/templates');
      setTemplates(response.data);
    } catch (err) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadTemplate = async (templateId, templateName, format = 'xlsx') => {
    try {
      const response = await axios.get(`/imports/templates/${templateId}/download?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${templateName}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download template');
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }
  
  if (templates.length === 0) {
    return (
      <div className="text-center py-12">
        <Download className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Available</h3>
        <p className="text-gray-600">Templates will appear here once created.</p>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {templates.map((template) => (
        <div key={template.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-purple-600" />
            </div>
            {template.is_public && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                Public
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
          <p className="text-sm text-gray-600 mb-4">{template.description}</p>
          <p className="text-sm text-gray-500 mb-4">{template.metric_count} metrics</p>
          <div className="flex gap-2">
            <button
              onClick={() => downloadTemplate(template.id, template.name, 'xlsx')}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition"
            >
              Download Excel
            </button>
            <button
              onClick={() => downloadTemplate(template.id, template.name, 'csv')}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition"
            >
              CSV
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Main Data Ingestion Page
function DataIngestionPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadedImport, setUploadedImport] = useState(null);
  
  const handleUploadSuccess = (importData) => {
    setUploadedImport(importData);
  };
  
  const handleImportComplete = () => {
    setUploadedImport(null);
    setActiveTab('history');
  };
  
  const tabs = [
    { id: 'upload', name: 'File Upload', icon: Upload },
    { id: 'manual', name: 'Manual Entry', icon: Plus },
    { id: 'history', name: 'Import History', icon: History },
    { id: 'templates', name: 'Templates', icon: Download }
  ];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard-v2')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Data Ingestion</h1>
                <p className="text-sm text-gray-600">Upload files or enter metric values manually</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {uploadedImport ? (
          <ImportPreview 
            importData={uploadedImport}
            onBack={() => setUploadedImport(null)}
            onComplete={handleImportComplete}
          />
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-8 border-b border-gray-200">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 font-medium transition border-b-2 -mb-px ${
                      activeTab === tab.id
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-600 border-transparent hover:text-gray-900'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.name}
                  </button>
                );
              })}
            </div>
            
            {/* Tab Content */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              {activeTab === 'upload' && <FileUpload onUploadSuccess={handleUploadSuccess} />}
              {activeTab === 'manual' && <ManualEntry />}
              {activeTab === 'history' && <ImportHistory />}
              {activeTab === 'templates' && <Templates />}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default DataIngestionPage;
